import {
  FC,
  HTMLAttributes,
  InputHTMLAttributes,
  useEffect,
  useState,
} from "react";
import clsx from "classnames";
import { useStyles } from "./TextField.style";
import { Typography } from "../Typography/Typography";
import {
  TypographyColor,
  TypographyVariant,
} from "../../../constants/typography";
import { Icon } from "../Icon/Icon";

export interface ITextFieldProps {
  label?: string;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  descriptionMessage?: string;
  inputProps?: InputHTMLAttributes<HTMLInputElement> & {
    "data-testid"?: string;
  };
  isShowClear?: boolean;
  maxLength?: number;
  isError?: boolean;
  onChangeValue?: (value: string) => void;
  endAdornment?: React.ReactNode;
}

export const TextField: FC<
  ITextFieldProps & HTMLAttributes<HTMLDivElement>
> = ({
  label,
  fullWidth,
  startIcon,
  descriptionMessage,
  isShowClear,
  inputProps,
  maxLength,
  isError,
  onChangeValue,
  endAdornment,
  ...otherProps
}) => {
  const classes = useStyles();
  const [value, setValue] = useState<string | number | readonly string[]>(
    inputProps?.value || "",
  );

  useEffect(() => {
    if (inputProps && inputProps.value !== undefined) {
      setValue(inputProps.value);
    }
  }, [inputProps?.value]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (
      maxLength &&
      value?.toString()?.length >= maxLength &&
      (event.nativeEvent as InputEvent).inputType !== "deleteContentBackward"
    )
      return;
    onChangeValue?.(event.target.value);
    setValue(event.target.value);
  };

  const onClear = () => {
    setValue("");
    onChangeValue?.("");
  };

  return (
    <div
      {...otherProps}
      className={clsx(
        classes.root,
        fullWidth && classes.fullWidth,
        otherProps.className,
      )}
    >
      {label && (
        <Typography
          variant={TypographyVariant.BodyB4Medium}
          color={TypographyColor.Default}
          data-testid="label-input"
          className={classes.label}
        >
          {label}
        </Typography>
      )}
      <div className={clsx(classes.content, !fullWidth && classes.fitContent)}>
        {startIcon && <div className={classes.startAdornment}>{startIcon}</div>}
        <input
          {...inputProps}
          className={clsx(
            classes.input,
            inputProps?.className,
            fullWidth && classes.fullWidth,
            isError && classes.errorBorder,
            inputProps?.disabled && classes.textDisable,
            startIcon && classes.hasStartIcon,
          )}
          placeholder={inputProps?.placeholder}
          value={value}
          onChange={onChange}
          disabled={inputProps?.disabled}
        />
        {isShowClear && value && (
          <Icon
            name="icon-bold-close-circle"
            className={classes.closeIcon}
            onClick={onClear}
            data-testid="clear-icon"
            size={20}
          />
        )}
        {endAdornment && (
          <div className={classes.endAdornment}>{endAdornment}</div>
        )}
      </div>
      {descriptionMessage && (
        <Typography
          variant={TypographyVariant.BodyB6Regular}
          color={isError ? TypographyColor.Error : TypographyColor.Silver}
          data-testid="error-text"
          className={classes.description}
        >
          {descriptionMessage}
        </Typography>
      )}
    </div>
  );
};
